import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import StripePayment from '../components/StripePayment';
import {
  Button,
  Container,
  Field,
  Input,
  Panel,
  Select,
  Textarea,
  TickItem
} from '../components/site/ui';
import { AdminOverlaysProvider, useToast } from '../components/admin/overlays';
import {
  IconAlert,
  IconBell,
  IconCheck,
  IconCheckCircle,
  IconClose,
  IconLock,
  IconNote,
  IconPackage,
  IconUpload
} from '../components/icons';
import { useCouriers } from '../utils/useCouriers';
import { fieldErrorsFrom, firstErrorFrom } from '../utils/formErrors';
import { formatUkPostcode } from '../utils/postcode';
import { getAcquisitionPayload, trackReportStarted, trackReportSubmitted } from '../utils/tracking';

/**
 * Step 1 asks exactly what the home page's hero card asks, so the two are one
 * flow rather than two that overlap: arriving from home skips straight past
 * it, and a direct visit to /report answers the same questions in place.
 */
const ISSUE_STEP_FIELDS = ['courier', 'trackingNumber', 'postcode', 'merchantName', 'issueType'];

// Mirrors the server's own checks (server/routes/cases.js) so a bad value is
// caught here, inline, instead of round-tripping to the backend first.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s()-]{7,20}$/;
const URL_RE = /^https?:\/\/.+/i;

/**
 * The merchant hand-off block on the confirmation step is intentionally disabled
 * (the magic link is emailed instead). Kept behind a named flag rather than a
 * bare `false &&` so the intent is readable.
 */
const SHOW_MERCHANT_LINK_HANDOFF = false;

/**
 * Evidence upload limits. These mirror multer's fileFilter and limits in
 * server/utils/fileUpload.js (and the `upload.array('files', 5)` cap in
 * server/routes/upload.js) — anything accepted here must be accepted there,
 * otherwise the file is only rejected after the case has already been created
 * and the customer never finds out.
 */
const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
const ACCEPTED_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.pdf'];
// Drag-and-drop can hand us a file with an empty `type` (and some systems
// report odd mime types for JPEGs), so the extension is checked as a fallback.
const ACCEPT_ATTR = [...ACCEPTED_MIMES, ...ACCEPTED_EXTS].join(',');

const isAcceptedFile = (file) => {
  if (ACCEPTED_MIMES.includes((file.type || '').toLowerCase())) return true;
  const name = (file.name || '').toLowerCase();
  return ACCEPTED_EXTS.some((ext) => name.endsWith(ext));
};

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const ReportPageInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const couriers = useCouriers();
  const toast = useToast();
  // The home page's hero card now requires the same five fields this step
  // does, so arriving from it always has all of them — this check is what
  // decides to skip step 1 in that case, and still falls back to asking here
  // for a direct visit (or an older link) with nothing, or only some, of them.
  const [step, setStep] = useState(() =>
    ISSUE_STEP_FIELDS.every((f) => String(location.state?.[f] || '').trim()) ? 2 : 1
  );
  const [formData, setFormData] = useState({
    trackingNumber: location.state?.trackingNumber || '',
    courier: location.state?.courier || '',
    postcode: location.state?.postcode || '',
    issueType: location.state?.issueType || '',
    customerName: '',
    customerEmail: '',
    merchantName: location.state?.merchantName || '',
    merchantWebsite: '',
    merchantEmail: '',
    orderNumber: '',
    dorSigned: false,
    files: [],
    comments: '',
    smsOptIn: false,
    phoneNumber: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [caseId, setCaseId] = useState(null);
  const [magicLink, setMagicLink] = useState(null);
  const [smsPrice, setSmsPrice] = useState(0.99);
  const [showPayment, setShowPayment] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const issueTypes = [
    { value: 'not_arrived', label: 'My parcel hasn\'t arrived yet' },
    { value: 'delivered_not_received', label: 'It says delivered, but I didn\'t receive it' },
    { value: 'damaged', label: 'My delivery was damaged' },
    { value: 'missing_items', label: 'Items are missing from the parcel' },
    { value: 'unsafe_place', label: 'It was left in an unsafe place' }
  ];

  // Clear step errors when leaving, and start each step at the top of the
  // page — this is all one route, so React Router's own navigation never
  // touches scroll position here; whatever the user had scrolled to on the
  // previous step otherwise carries straight over.
  useEffect(() => {
    if (step !== 2 && step !== 5) setFieldErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Fetch SMS price on mount (public endpoint, no auth)
  useEffect(() => {
    const fetchSmsPrice = async () => {
      try {
        const response = await api.get('/api/merchants/pricing');
        if (response.data.smsPrice != null) {
          setSmsPrice(response.data.smsPrice);
        }
      } catch (error) {
        console.error('Error fetching SMS price:', error);
      }
    };
    fetchSmsPrice();
  }, []);

  const handleInputChange = (e) => {
    trackReportStarted();
    const { name, value, type, checked } = e.target;
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'postcode' ? formatUkPostcode(value) : value
    }));
  };

  // Delivery step (step 2) field-by-field validation, mirroring what the
  // backend will also reject — lets the error land under the input that
  // caused it instead of only a generic banner at submit time.
  const validateDeliveryStep = () => {
    const errors = {};
    if (!formData.customerName.trim()) errors.customerName = 'Your name is required';
    if (!formData.customerEmail.trim()) errors.customerEmail = 'Email is required';
    else if (!EMAIL_RE.test(formData.customerEmail.trim())) errors.customerEmail = 'Enter a valid email address';
    if (!formData.merchantEmail.trim()) errors.merchantEmail = 'Merchant email is required';
    else if (!EMAIL_RE.test(formData.merchantEmail.trim())) errors.merchantEmail = 'Enter a valid email address';
    if (!formData.orderNumber.trim()) errors.orderNumber = 'Order number is required';
    if (formData.merchantWebsite.trim() && !URL_RE.test(formData.merchantWebsite.trim())) {
      errors.merchantWebsite = 'Enter a valid website URL (starting with http:// or https://)';
    }
    return errors;
  };

  /**
   * Shared by the file picker and the drop zone. Everything rejected is named
   * back to the customer rather than dropped quietly, so "I attached it and it
   * vanished" can't happen.
   */
  const addFiles = (incoming) => {
    const rejected = [];
    // Built from the rendered state rather than inside a setState updater:
    // the updater can run twice (StrictMode) and would double every message.
    const accepted = [...formData.files];
    for (const file of incoming) {
      if (!isAcceptedFile(file)) {
        rejected.push(`${file.name} — not a supported file type`);
      } else if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name} — ${formatBytes(file.size)}, over the 10MB limit`);
      } else if (accepted.some(f => f.name === file.name && f.size === file.size)) {
        rejected.push(`${file.name} — already added`);
      } else if (accepted.length >= MAX_FILES) {
        rejected.push(`${file.name} — only ${MAX_FILES} files can be attached`);
      } else {
        accepted.push(file);
      }
    }
    setFormData(prev => ({ ...prev, files: accepted }));
    setFileErrors(rejected);
  };

  const handleFileChange = (e) => {
    trackReportStarted();
    addFiles(Array.from(e.target.files));
    // Clear the input so removing a file and re-picking the same one still
    // fires a change event.
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFormData(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }));
    setFileErrors([]);
  };

  const handleDragOver = (e) => {
    // Without preventDefault on dragover the browser navigates to the file
    // instead of firing a drop event — this is what made the advertised
    // drag & drop do nothing.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = (e) => {
    // Moving over a child element fires dragleave on the zone too; ignore
    // those so the highlight doesn't flicker.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) {
      trackReportStarted();
      addFiles(dropped);
    }
  };

  // Build case payload (used by handleStep5Continue and submitCase)
  const buildCasePayload = (opts = {}) => {
    const { paymentIntentId, skipSms } = opts;
    const smsOptIn = skipSms
      ? { optedIn: false }
      : {
          optedIn: formData.smsOptIn,
          phoneNumber: formData.smsOptIn ? formData.phoneNumber : undefined
        };
    const payload = {
      customer: {
        name: formData.customerName,
        email: formData.customerEmail,
        phone: formData.phoneNumber || undefined
      },
      issueType: formData.issueType,
      deliveryDetails: {
        trackingNumber: formData.trackingNumber,
        courier: formData.courier,
        merchantName: formData.merchantName,
        merchantWebsite: formData.merchantWebsite || undefined,
        merchantEmail: formData.merchantEmail || undefined,
        orderNumber: formData.orderNumber,
        postcode: formData.postcode?.trim() || undefined
      },
      dor: formData.issueType === 'delivered_not_received' && formData.dorSigned ? {
        signed: true,
        declaration: 'I confirm that I have not received this parcel and that the information I have provided is true and accurate to the best of my knowledge.'
      } : undefined,
      evidence: { comments: formData.comments || undefined },
      smsOptIn,
      // Campaign attribution only — stored with the case, never sent to Reddit.
      acquisition: getAcquisitionPayload()
    };
    if (paymentIntentId) payload.paymentIntentId = paymentIntentId;
    return payload;
  };

  // Create case, upload files, get magic link, then go to confirmation (used after payment/skip on step 6 or direct submit when not opted in)
  const submitCase = async (opts = {}) => {
    setSubmitting(true);
    try {
      const caseData = buildCasePayload(opts);
      const response = await api.post('/api/cases', caseData);
      const createdCase = response.data;
      setCaseId(createdCase.caseId);
      // The conversion fires here and only here: after the backend has
      // confirmed the case exists.
      trackReportSubmitted(createdCase.acquisition?.conversionId);
      toast('Your report has been submitted.', { tone: 'success' });

      if (formData.files?.length > 0) {
        const formDataUpload = new FormData();
        formData.files.forEach(file => formDataUpload.append('files', file));
        try {
          await api.post(`/api/upload/${createdCase.caseId}`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          // The case itself is already created, so this isn't fatal — but the
          // customer needs to know their evidence didn't make it rather than
          // assuming it did.
          toast('Your report was submitted, but the attachments could not be uploaded. You can email them to us instead.', { tone: 'error' });
        }
      }

      try {
        const linkResponse = await api.post(`/api/merchants/case/${createdCase.caseId}/magic-link`);
        setMagicLink(linkResponse.data.magicLink);
      } catch (linkError) {
        console.error('Error generating magic link:', linkError);
      }

      // Go to feedback page after successful submission
      navigate('/feedback', { state: { caseId: createdCase.caseId } });
    } catch (error) {
      console.error('Error submitting case:', error);
      // Step 1/2 already enforce these fields client-side, so a validation
      // error here only happens via a stale session or a direct API call —
      // still worth surfacing the real reason instead of a generic fallback.
      const message = firstErrorFrom(
        fieldErrorsFrom(error, { fallback: 'Error submitting case. Please try again.' })
      );
      toast(message || 'Error submitting case. Please try again.', { tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Step 5 continue: Next (when opted in) or Submit Report (when not). Not used as form submit to avoid nested form — Pay button must only submit Stripe form.
  const handleStep5Continue = async () => {
    if (formData.smsOptIn && formData.phoneNumber?.trim()) {
      if (!PHONE_RE.test(formData.phoneNumber.trim())) {
        setFieldErrors(prev => ({ ...prev, phoneNumber: 'Enter a valid phone number' }));
        return;
      }
      setStep(6);
      setShowPayment(true);
      return;
    }
    await submitCase();
  };

  /* ── Progress ────────────────────────────────────────────────────────────── */

  const needsDeclaration = formData.issueType === 'delivered_not_received';
  const flowSteps = [
    { id: 1, label: 'Parcel' },
    { id: 2, label: 'Details' },
    ...(needsDeclaration ? [{ id: 3, label: 'Declaration' }] : []),
    { id: 4, label: 'Evidence' },
    { id: 5, label: 'Updates' },
    ...(showPayment ? [{ id: 6, label: 'Payment' }] : [])
  ];
  const currentIndex = flowSteps.findIndex((s) => s.id === step);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <h2 className="font-display text-xl font-700 tracking-tight text-ink-900 sm:text-2xl">
              Report a delivery issue
            </h2>
            <p className="mt-1.5 text-sm text-ink-600">
              Start with the parcel and what went wrong — the rest follows.
            </p>

            <div className="mt-6 space-y-5">
              <Field label="Choose your courier" htmlFor="courier" required>
                <Select
                  id="courier"
                  name="courier"
                  value={formData.courier}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select courier</option>
                  {couriers.map((c) => (
                    <option key={c._id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Tracking number" htmlFor="trackingNumber" required>
                <Input
                  type="text"
                  id="trackingNumber"
                  name="trackingNumber"
                  value={formData.trackingNumber}
                  onChange={handleInputChange}
                  placeholder="e.g. H00ABC123456"
                  required
                />
              </Field>

              <Field label="Postcode" htmlFor="postcode" required>
                <Input
                  type="text"
                  id="postcode"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  placeholder="e.g. SW1A 1AA"
                  autoComplete="postal-code"
                  required
                />
              </Field>

              <Field label="Merchant / Retailer" htmlFor="merchantName" required>
                <Input
                  type="text"
                  id="merchantName"
                  name="merchantName"
                  value={formData.merchantName}
                  onChange={handleInputChange}
                  placeholder="e.g. Zara, ASOS, John Lewis"
                  required
                />
              </Field>

              <Field label="What's the problem?" htmlFor="issueType" required>
                <Select
                  id="issueType"
                  name="issueType"
                  value={formData.issueType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select issue type</option>
                  {issueTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Button
              onClick={() => {
                const missing = ISSUE_STEP_FIELDS.some((f) => !String(formData[f] || '').trim());
                if (missing) {
                  toast('Please fill in all required fields to continue.', { tone: 'error' });
                  return;
                }
                setStep(2);
              }}
              size="lg"
              className="mt-6 w-full"
            >
              Continue
            </Button>
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="font-display text-xl font-700 tracking-tight text-ink-900 sm:text-2xl">
              Delivery details
            </h2>
            <p className="mt-1.5 text-sm text-ink-600">
              These details go straight into the evidence pack for the retailer.
            </p>

            {/* What step 1 captured — most people reach this step from the
                home page, where they answered it on a different screen. */}
            <div className="mt-5 flex flex-wrap items-center gap-2 rounded-xl bg-ink-50 px-3.5 py-3">
              {[
                formData.courier,
                formData.trackingNumber,
                formData.postcode,
                formData.merchantName,
                issueTypes.find((t) => t.value === formData.issueType)?.label
              ]
                .filter(Boolean)
                .map((value) => (
                  <span
                    key={value}
                    className="rounded-full bg-white px-2.5 py-1 text-xs font-600 text-ink-700 ring-1 ring-inset ring-ink-200"
                  >
                    {value}
                  </span>
                ))}
              <button
                type="button"
                onClick={() => setStep(1)}
                className="ml-auto rounded text-xs font-600 text-brand-700 underline transition-colors hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
              >
                Edit
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Your name" htmlFor="customerName" required error={fieldErrors.customerName}>
                <Input
                  type="text"
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  autoComplete="name"
                  required
                />
              </Field>
              <Field label="Email" htmlFor="customerEmail" required error={fieldErrors.customerEmail}>
                <Input
                  type="email"
                  id="customerEmail"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  autoComplete="email"
                  required
                />
              </Field>
              <Field label="Merchant email" htmlFor="merchantEmail" required error={fieldErrors.merchantEmail}>
                <Input
                  type="email"
                  id="merchantEmail"
                  name="merchantEmail"
                  value={formData.merchantEmail}
                  onChange={handleInputChange}
                  required
                />
              </Field>
              <Field label="Order number" htmlFor="orderNumber" required error={fieldErrors.orderNumber}>
                <Input
                  type="text"
                  id="orderNumber"
                  name="orderNumber"
                  value={formData.orderNumber}
                  onChange={handleInputChange}
                  required
                />
              </Field>
              <Field
                label="Merchant website"
                htmlFor="merchantWebsite"
                hint="Optional"
                error={fieldErrors.merchantWebsite}
              >
                <Input
                  type="url"
                  id="merchantWebsite"
                  name="merchantWebsite"
                  value={formData.merchantWebsite}
                  onChange={handleInputChange}
                  placeholder="https://"
                />
              </Field>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button variant="secondary" size="lg" onClick={() => setStep(1)} className="sm:flex-1">
                Back
              </Button>
              <Button
                size="lg"
                className="sm:flex-1"
                onClick={() => {
                  const errors = validateDeliveryStep();
                  if (Object.keys(errors).length) {
                    setFieldErrors(prev => ({ ...prev, ...errors }));
                    toast('Please fix the highlighted fields to continue.', { tone: 'error' });
                    return;
                  }
                  setStep(formData.issueType === 'delivered_not_received' ? 3 : 4);
                }}
              >
                Continue
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="font-display text-xl font-700 tracking-tight text-ink-900 sm:text-2xl">
                Important declaration
              </h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-700">
                To proceed with a &quot;Delivered but not received&quot; report, please confirm the
                following:
              </p>

              <blockquote className="mt-4 rounded-r-xl border-l-[3px] border-brand-700 bg-brand-50/70 px-4 py-3.5 text-[15px] italic leading-relaxed text-ink-800">
                I confirm that I have not received this parcel and that the information I have
                provided is true and accurate to the best of my knowledge.
              </blockquote>

              <p className="mt-4 text-[15px] leading-relaxed text-ink-700">
                I understand that this declaration is timestamped, linked to my order and tracking
                details, and may be shared with the merchant or courier as part of an investigation.
              </p>

              <label
                className={`mt-5 flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-colors ${
                  formData.dorSigned
                    ? 'border-brand-800 bg-brand-50/60'
                    : 'border-ink-200 hover:border-ink-300'
                }`}
              >
                <input
                  type="checkbox"
                  name="dorSigned"
                  checked={formData.dorSigned}
                  onChange={handleInputChange}
                  className="mt-0.5 h-5 w-5 shrink-0 rounded accent-brand-800"
                />
                <span className="text-[15px] font-600 text-ink-900">I confirm and agree</span>
              </label>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Button variant="secondary" size="lg" onClick={() => setStep(2)} className="sm:flex-1">
                  Back
                </Button>
                <Button
                  size="lg"
                  className="sm:flex-1"
                  onClick={() => formData.dorSigned && setStep(4)}
                  disabled={!formData.dorSigned}
                >
                  <IconLock size={17} />
                  Continue
                </Button>
              </div>

              <p className="mt-4 text-xs text-ink-500">
                No ID upload required • No payment details collected
              </p>
            </div>

            <div className="border-t border-ink-200 pt-6">
              <h3 className="font-display text-base font-700 tracking-tight text-ink-900">
                About this customer declaration
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-700">
                This declaration replaces informal delivery issue emails with a structured,
                timestamped statement from the customer. Each declaration:
              </p>
              <ul className="mt-4 space-y-3">
                <TickItem>Is linked to the order and tracking number</TickItem>
                <TickItem>Is signed digitally at the time of submission</TickItem>
                <TickItem>Includes a confirmation of accuracy by the customer</TickItem>
                <TickItem>Can be shared with the courier as part of an escalation</TickItem>
              </ul>
              <p className="mt-4 text-sm leading-relaxed text-ink-700">
                This helps merchants review claims faster and provides a clearer audit trail if
                further investigation is required.
              </p>

              <div className="mt-5 flex items-center gap-3 rounded-xl bg-ink-100 px-4 py-3.5">
                <img src="/brand.png" alt="ParcelBuddy" className="h-10 w-auto shrink-0 sm:h-11" />
                <p className="min-w-0 text-xs leading-relaxed text-ink-700 sm:text-sm">
                  ParcelBuddy does not determine outcomes — merchants remain in full control of the
                  decision.
                </p>
              </div>

              {formData.dorSigned && (
                <div className="mt-4 flex items-center gap-3 rounded-xl bg-success-50 px-4 py-3.5 ring-1 ring-inset ring-success-100">
                  <IconCheckCircle size={26} className="shrink-0 text-success-600" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="font-display text-sm font-700 text-ink-900">
                      Customer declaration signed
                    </p>
                    <p className="mt-0.5 text-xs text-ink-600">
                      Timestamped • Order-linked • Evidence-ready
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h2 className="font-display text-xl font-700 tracking-tight text-ink-900 sm:text-2xl">
              Evidence upload
            </h2>
            <p className="mt-1.5 text-sm text-ink-600">
              Optional, but it speeds up the retailer&apos;s decision.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-ink-600">
              You can upload: order confirmation (PDF/image), delivery photo, invoice, screenshots.
            </p>

            <input
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              onDragEnter={handleDragOver}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
                dragActive
                  ? 'border-brand-600 bg-brand-50'
                  : 'border-ink-300 bg-ink-50/60 hover:border-brand-600 hover:bg-brand-50/40'
              }`}
            >
              <span className="pointer-events-none flex h-11 w-11 items-center justify-center rounded-full bg-white text-brand-700 ring-1 ring-inset ring-ink-200">
                <IconUpload size={20} />
              </span>
              <span className="pointer-events-none mt-3 text-[15px] font-600 text-ink-800">
                {dragActive ? 'Drop your files to attach them' : 'Drag & drop files here or click to browse'}
              </span>
              <span className="pointer-events-none mt-1.5 text-xs text-ink-500">
                JPG, PNG, GIF or PDF — up to {MAX_FILES} files, 10MB each
              </span>
            </label>

            {fileErrors.length > 0 && (
              <div
                role="alert"
                className="mt-3 flex items-start gap-2.5 rounded-lg bg-danger-50 px-3.5 py-3 ring-1 ring-inset ring-danger-100"
              >
                <IconAlert size={16} className="mt-0.5 shrink-0 text-danger-600" />
                <div className="min-w-0 text-xs leading-relaxed text-danger-700">
                  <p className="font-600">
                    {fileErrors.length === 1 ? "This file wasn't added:" : "These files weren't added:"}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {fileErrors.map((message, idx) => (
                      <li key={idx} className="break-words">{message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {formData.files.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-600 text-ink-800">
                  Selected files ({formData.files.length}/{MAX_FILES})
                </p>
                <ul className="mt-2 space-y-2">
                  {formData.files.map((file, idx) => (
                    <li
                      key={`${file.name}-${file.size}-${idx}`}
                      className="flex items-center gap-2.5 rounded-lg border border-ink-200 bg-white px-3.5 py-2.5"
                    >
                      <IconNote size={16} className="shrink-0 text-ink-500" />
                      <span className="min-w-0 flex-1 truncate text-sm text-ink-700">{file.name}</span>
                      <span className="shrink-0 text-xs text-ink-500">{formatBytes(file.size)}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        aria-label={`Remove ${file.name}`}
                        className="shrink-0 rounded-md p-1 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
                      >
                        <IconClose size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Field label="Additional comments" htmlFor="comments" className="mt-6">
              <Textarea
                id="comments"
                name="comments"
                value={formData.comments}
                onChange={handleInputChange}
                rows={4}
                placeholder="Anything else the retailer should know…"
              />
            </Field>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button
                variant="secondary"
                size="lg"
                className="sm:flex-1"
                onClick={() => formData.issueType === 'delivered_not_received' ? setStep(3) : setStep(2)}
              >
                Back
              </Button>
              <Button size="lg" className="sm:flex-1" onClick={() => setStep(5)}>
                Continue
              </Button>
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold-50 text-gold-700 ring-1 ring-inset ring-gold-100">
                <IconBell size={20} />
              </span>
              <div className="min-w-0">
                <h2 className="font-display text-xl font-700 tracking-tight text-ink-900 sm:text-2xl">
                  Get instant updates by text
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-600">
                  Know exactly when your case moves forward — no need to keep checking email.
                </p>
              </div>
            </div>

            <ul className="mt-6 space-y-3 rounded-xl bg-ink-50 p-5">
              <TickItem>When the merchant reviews your case</TickItem>
              <TickItem>When it&apos;s escalated to the courier</TickItem>
              <TickItem>When a decision is made (refund / reship / response)</TickItem>
            </ul>

            <p className="mt-5 text-sm leading-relaxed text-ink-700">
              Most cases resolve within days — SMS keeps you informed in real time.
            </p>

            <div className="mt-5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <p className="font-display text-xl font-800 tracking-tight text-ink-900">
                £{smsPrice.toFixed(2)}
              </p>
              <p className="text-sm text-ink-600">one-time, per case</p>
            </div>
            <p className="mt-0.5 text-xs text-ink-500">No subscription. No recurring charges.</p>

            <label
              className={`mt-6 flex cursor-pointer items-start gap-3 rounded-xl border-2 p-5 transition-colors ${
                formData.smsOptIn ? 'border-brand-800 bg-brand-50/60' : 'border-ink-200 hover:border-ink-300'
              }`}
            >
              <input
                type="checkbox"
                name="smsOptIn"
                checked={formData.smsOptIn}
                onChange={handleInputChange}
                className="mt-0.5 h-5 w-5 shrink-0 rounded accent-brand-800"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-600 text-ink-900">
                  Send me SMS updates (£{smsPrice.toFixed(2)})
                </p>
                <p className="mt-0.5 text-sm text-ink-600">
                  I agree to receive text message updates about this case only.
                </p>
                {formData.smsOptIn && (
                  <div className="mt-4">
                    <Input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="+44 7XXX XXXXXX"
                      autoComplete="tel"
                      required={formData.smsOptIn}
                      aria-invalid={fieldErrors.phoneNumber ? true : undefined}
                    />
                    {fieldErrors.phoneNumber && (
                      <p role="alert" className="mt-1.5 text-xs font-600 text-danger-700">
                        {fieldErrors.phoneNumber}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </label>

            {/* Next (when opted in) or Submit Report (when not). type="button" so Pay on step 6 never submits a parent form. */}
            <Button
              disabled={submitting || (formData.smsOptIn && !formData.phoneNumber?.trim())}
              loading={submitting}
              onClick={handleStep5Continue}
              size="lg"
              className="mt-6 w-full"
            >
              {submitting
                ? 'Submitting…'
                : formData.smsOptIn && formData.phoneNumber?.trim()
                  ? 'Next'
                  : 'Submit report without SMS'}
            </Button>

            <p className="mt-3 text-center text-xs leading-relaxed text-ink-500">
              {formData.smsOptIn && formData.phoneNumber?.trim()
                ? 'Complete payment on the next step to enable SMS — or skip to continue with email only.'
                : 'You can add SMS updates now — or continue with email only.'}
            </p>

            <Button variant="secondary" size="lg" onClick={() => setStep(4)} className="mt-4 w-full">
              Back
            </Button>
          </div>
        );

      case 6:
        // Payment step (case not created yet — submitted only after payment or skip)
        return (
          <div>
            <h2 className="font-display text-xl font-700 tracking-tight text-ink-900 sm:text-2xl">
              Complete SMS payment
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-600">
              To enable SMS updates, please complete the payment below. Your report will be submitted
              after payment.
            </p>

            <div className="mt-6">
              {showPayment && formData.phoneNumber && (
                <StripePayment
                  caseId={undefined}
                  phoneNumber={formData.phoneNumber}
                  amount={smsPrice}
                  postcode={formData.postcode}
                  onSuccess={(paymentIntent) => {
                    submitCase({ paymentIntentId: paymentIntent.id }).then(() => {
                      setShowPayment(false);
                      // submitCase navigates to the feedback page; no step 7
                    });
                  }}
                  onError={(error) => {
                    console.error('Payment error:', error);
                    toast('Payment failed. You can try again or skip to continue without SMS updates.', {
                      tone: 'error'
                    });
                  }}
                />
              )}
            </div>

            <Button
              variant="secondary"
              size="lg"
              disabled={submitting}
              loading={submitting}
              onClick={() => submitCase({ skipSms: true })}
              className="mt-4 w-full"
            >
              {submitting ? 'Submitting…' : 'Skip payment (submit without SMS)'}
            </Button>
          </div>
        );

      case 7:
        // Confirmation (after payment or skip, or when SMS not opted in)
        return (
          <div>
            <div className="text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-50 text-success-600 ring-1 ring-inset ring-success-100">
                <IconCheckCircle size={32} strokeWidth={1.5} />
              </span>
              <h2 className="mt-6 font-display text-2xl font-800 tracking-tight text-ink-900">
                Your report has been submitted.
              </h2>
              <p className="mt-2 text-lg text-ink-600">Case ID: #{caseId}</p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink-600">
                We&apos;re preparing an evidence pack for the merchant. You&apos;ll receive an email
                confirmation shortly.
              </p>
            </div>

            {SHOW_MERCHANT_LINK_HANDOFF && magicLink && (
              <div className="mt-8 rounded-xl bg-ink-50 p-6">
                <h3 className="font-display text-base font-700 text-ink-900">
                  Your delivery issue pack is ready
                </h3>
                <p className="mt-2 text-sm text-ink-700">
                  We&apos;ve prepared a structured delivery issue pack containing your statement,
                  uploaded evidence, courier tracking history and the declaration of non-receipt
                  (if applicable).
                </p>
                <p className="mt-3 text-sm text-ink-700">
                  To resolve this, please share the secure link below with the merchant&apos;s
                  support team.
                </p>
                <div className="mt-4 rounded-lg border border-ink-200 bg-white p-4">
                  <p className="text-sm font-600 text-ink-800">Secure merchant link</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Input
                      type="text"
                      value={magicLink}
                      readOnly
                      className="flex-1 bg-ink-50 font-mono text-xs"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      size="md"
                      onClick={() => navigator.clipboard.writeText(magicLink)}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-ink-500">
                    This link is safe to share with the merchant. No payment details are included.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                onClick={() => {
                  // Reset all form state
                  setStep(1);
                  setFormData({
                    trackingNumber: location.state?.trackingNumber || '',
                    courier: location.state?.courier || '',
                    issueType: '',
                    customerName: '',
                    customerEmail: '',
                    postcode: '',
                    merchantName: '',
                    merchantWebsite: '',
                    merchantEmail: '',
                    orderNumber: '',
                    dorSigned: false,
                    files: [],
                    comments: '',
                    smsOptIn: false,
                    phoneNumber: ''
                  });
                  setCaseId(null);
                  setMagicLink(null);
                  setSubmitting(false);
                  setShowPayment(false);
                  setFieldErrors({});
                  setFileErrors([]);
                }}
                size="lg"
              >
                Submit another case
              </Button>
              <Button variant="secondary" size="lg" onClick={() => navigate('/')}>
                Done
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  /**
   * The confirmation step (7) is its own full-width moment — it has nothing to
   * reassure you about any more, so the supporting rail would just be noise.
   */
  const isConfirmation = step === 7;

  return (
    <Container width="full" className="py-7 sm:py-9">
      {isConfirmation ? (
        <div className="mx-auto max-w-2xl">
          <Panel>{renderStep()}</Panel>
        </div>
      ) : (
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] lg:gap-12">
          {/* Supporting rail: the form used to sit alone in the middle of a
              wide empty page. This also keeps the reassurance visible while
              someone is deciding whether to hand over their details. */}
          <aside className="lg:sticky lg:top-28 lg:pt-1">
            <p className="text-[11px] font-700 uppercase tracking-[0.14em] text-brand-600">
              Report a delivery issue
            </p>
            <h1 className="mt-2.5 font-display text-[26px] font-800 leading-[1.12] tracking-tight text-ink-900 sm:text-[30px]">
              Let&apos;s get this to the <span className="text-accent-500">right place.</span>
            </h1>

            <ol className="mt-6 space-y-3">
              {flowSteps.map((s, i) => {
                const done = i < currentIndex;
                const active = i === currentIndex;
                return (
                  <li key={s.id} className="flex items-center gap-3">
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-700 ${
                        active
                          ? 'bg-accent-500 text-white'
                          : done
                            ? 'bg-brand-700 text-white'
                            : 'bg-ink-100 text-ink-500'
                      }`}
                    >
                      {done ? <IconCheck size={13} /> : i + 1}
                    </span>
                    <span
                      className={`text-sm ${
                        active ? 'font-700 text-ink-900' : done ? 'font-600 text-ink-700' : 'text-ink-500'
                      }`}
                    >
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ol>

            <ul className="mt-7 space-y-2.5 border-t border-ink-200 pt-5">
              {[
                { icon: IconLock, label: 'Free — no account required' },
                { icon: IconPackage, label: 'Goes to the retailer, not the courier' },
                { icon: IconCheckCircle, label: 'Evidence attached automatically' }
              ].map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5 text-sm text-ink-600">
                  <Icon size={15} className="shrink-0 text-brand-600" />
                  {label}
                </li>
              ))}
            </ul>
          </aside>

          <Panel className="p-5 sm:p-6">{renderStep()}</Panel>
        </div>
      )}
    </Container>
  );
};

// Wrapped here (rather than in App.js) so the page stays self-contained —
// this route sits inside the public Layout, which has no toast stack of its
// own, same as the standalone /merchant/case/:token route.
const ReportPage = () => (
  <AdminOverlaysProvider>
    <ReportPageInner />
  </AdminOverlaysProvider>
);

export default ReportPage;
